'use client';

import type { ReactNode } from 'react';
import { CliEditor } from '../../../../artifacts/cli/CliEditor';
import { parseTerminalOutput } from './terminal-output-parser';
import {
  getTerminalDescription,
  getTerminalDisplayInput,
} from './terminal-input-parser';
import type { TerminalResult } from '../../hitl-types';
import type { CardRenderContext } from '../../tool-config-types';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import { HitlApprovalFooter } from '../../HITL/components/HitlApprovalFooter';
import { withTerminalApprovalBadges } from './terminal-auto-approval-badge';
import { getTerminalSessionInfo } from './terminal-session-info';
import { withTerminalSessionBadge } from './terminal-session-badge';

export const renderTerminalCard = (ctx: CardRenderContext): ReactNode => {
  const parsed = parseTerminalOutput(ctx.toolCallData.output);
  const command = getTerminalDisplayInput(ctx.toolCallData.input) ?? '';
  const result = parsed.displayData as TerminalResult | undefined;
  const isRejectedHITL = ctx.hitlDecisionType === 'reject';
  const pendingApproval = ctx.pendingApproval;
  const duration =
    parsed.timing?.durationMs ??
    ctx.toolCallData.timing?.durationMs ??
    undefined;

  if (isRejectedHITL || !command) {
    return null;
  }

  const toolCallId = ctx.toolCallData.toolCallId ?? ctx.toolCallData.id;
  const sessionInfo = pendingApproval
    ? undefined
    : getTerminalSessionInfo({
        input: ctx.toolCallData.input,
        output: ctx.toolCallData.output,
      });
  const hasRun = !pendingApproval && (Boolean(result) || Boolean(sessionInfo));

  return (
    <CliEditor
      command={command}
      title={getTerminalDescription(ctx.toolCallData.input)}
      result={result}
      duration={duration}
      commandInitiallyOpen
      outputInitiallyOpen={false}
      hideCommandToggle={Boolean(pendingApproval)}
      expansionKey={toolCallId ? `terminal-card:${toolCallId}` : undefined}
      terminalTarget={
        hasRun
          ? {
              toolCallId,
              ptyId:
                sessionInfo?.kind === 'interactive'
                  ? sessionInfo.ptyId
                  : undefined,
            }
          : undefined
      }
      headerActions={withTerminalApprovalBadges({
        input: ctx.toolCallData.input,
        actions: withTerminalSessionBadge({
          info: sessionInfo,
          actions: getHITLArtifactHeaderActions(ctx),
        }),
      })}
      footer={
        pendingApproval ? <HitlApprovalFooter {...pendingApproval} /> : null
      }
    />
  );
};
